import React, { useState, useCallback } from 'react';
import { useProfile } from '../hooks/useProfile';
import Input from '@/components/form/input/InputField';
import Button from '@/components/ui/button/Button';
import { UpdateUserProfileDto, UpdateEmployeeProfileDto } from '../api/profileApi';
import Label from '@/components/form/Label';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';

interface EditProfileFormProps {
  onSubmit: () => void;
  onClose: () => void;
  initialData: any;
  currentData: any;
  onDataChange?: (data: any) => void;
}

const EditProfileForm = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, EditProfileFormProps>(({
  onSubmit,
  onClose,
  initialData: _initialData,
  currentData: _currentData,
  onDataChange
}, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { userProfile, employee, updateUserProfile, updateEmployeeProfile } = useProfile();
  const [formData, setFormData] = useState({
    first_name: userProfile?.first_name || '',
    last_name: userProfile?.last_name || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    telegram_username: employee?.telegram_username || ''
  });

  React.useEffect(() => {
    onDataChange?.(formData);
  }, [formData, onDataChange]);

  const handleSubmit = async () => {
    try {
      const userProfileData: UpdateUserProfileDto = {
        first_name: formData.first_name,
        last_name: formData.last_name
      };

      const employeeProfileData: UpdateEmployeeProfileDto = {
        email: formData.email,
        phone: formData.phone,
        telegram_username: formData.telegram_username || null
      };

      await Promise.all([
        updateUserProfile.mutateAsync(userProfileData),
        updateEmployeeProfile.mutateAsync(employeeProfileData)
      ]);

      onSubmit();
      toast.success(t('profile.edit.save_success'));
    } catch (error) {
      toast.error(t('profile.edit.save_error') + `<br/><br/>${error}`);
    }
  };

  return (
    <>
      <div className="p-6">
        <h5 className="text-xl font-semibold text-gray-800 dark:text-white/90 lg:text-2xl">
          {t('profile.edit.title')}
        </h5>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('profile.edit.description')}
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>
                {t('profile.edit.fields.first_name')}
              </Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder={t('profile.edit.placeholders.first_name')}
              />
            </div>

            <div>
              <Label>
                {t('profile.edit.fields.last_name')}
              </Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder={t('profile.edit.placeholders.last_name')}
              />
            </div>

            <div>
              <Label>
                {t('profile.edit.fields.email')}
              </Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t('profile.edit.placeholders.email')}
              />
            </div>

            <div>
              <Label>
                {t('profile.edit.fields.phone')}
              </Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={t('profile.edit.placeholders.phone')}
              />
            </div>

            <div>
              <Label>
                {t('profile.edit.fields.telegram')}
              </Label>
              <Input
                value={formData.telegram_username}
                onChange={(e) => setFormData(prev => ({ ...prev, telegram_username: e.target.value }))}
                placeholder={t('profile.edit.placeholders.telegram')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
          >
            {t('profile.edit.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={updateUserProfile.isPending || updateEmployeeProfile.isPending}
          >
            {updateUserProfile.isPending || updateEmployeeProfile.isPending ? t('profile.edit.buttons.saving') : t('profile.edit.buttons.save')}
          </Button>
        </div>
      </div>
    </>
  );
});

EditProfileForm.displayName = 'EditProfileForm';

const ProtectedEditProfileForm = withFormProtection(EditProfileForm);

interface EditProfileFormWrapperProps extends Omit<EditProfileFormProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const EditProfileFormWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, EditProfileFormWrapperProps>((props, ref) => {
  const { userProfile, employee } = useProfile();
  const initialData = {
    first_name: userProfile?.first_name || '',
    last_name: userProfile?.last_name || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    telegram_username: employee?.telegram_username || ''
  };
  const [currentData, setCurrentData] = useState(initialData);

  const handleDataChange = useCallback((data: any) => {
    setCurrentData(data);
  }, []);

  return (
    <ProtectedEditProfileForm
      {...props}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

EditProfileFormWrapper.displayName = 'EditProfileFormWrapper';

export default EditProfileFormWrapper;

// Export the unprotected version for testing or special cases
export { EditProfileForm as UnprotectedEditProfileForm };
